import { useState } from 'react';
import { Address } from '@weave/schema-gen-ts/dist/schemas/phone/address/v1/address_service.pb';
import { E911Api } from '@frontend/api-e911-addresses';
import { CountryCodes, usOptions } from '@frontend/geography';
import { useTranslation } from '@frontend/i18n';
import { useMutation } from '@frontend/react-query-helpers';
import { FormFieldActionTypes, PostalCodeLocale, useForm } from '@frontend/design-system';
import { isInvalidE911AddressError } from '../../../utils/e911-address';
import { logError } from '../../../utils/loggers';

type AddressSaveErrorVariant = 'invalid-address' | 'generic';

export type AddressSaveError = {
  key: number;
  variant: AddressSaveErrorVariant;
};

type CreatedE911Address = Awaited<ReturnType<typeof E911Api.create>>;

export const getFullStateName = (state?: string) => {
  if (!state) {
    return undefined;
  }

  const trimmedState = state.trim();
  const normalizedState = trimmedState.toUpperCase();

  return usOptions.find(({ value }) => value.toUpperCase() === normalizedState)?.label ?? trimmedState;
};

export const getE911CreateSuccessMessage = (
  t: (key: string, params?: Record<string, string | number>) => string,
  submittedAddress?: Partial<Address>,
  createdAddress?: Partial<Address>
) => {
  const submittedState = submittedAddress?.state?.trim();
  const correctedState = createdAddress?.state?.trim();
  const submittedPostalCode = submittedAddress?.postalCode?.trim();
  const correctedPostalCode = createdAddress?.postalCode?.trim();
  const normalizedSubmittedState = submittedState?.toUpperCase();
  const normalizedCorrectedState = correctedState?.toUpperCase();

  if (submittedState && correctedState && normalizedSubmittedState !== normalizedCorrectedState) {
    const fullCorrectedStateName = getFullStateName(correctedState);
    return t('Address added successfully. State was autocorrected to "{{state}}".', {
      state: fullCorrectedStateName ?? correctedState,
    });
  }

  if (submittedPostalCode && correctedPostalCode && submittedPostalCode !== correctedPostalCode) {
    return t('Address added successfully. Postal Code autocorrected to "{{postalCode}}".', {
      postalCode: correctedPostalCode,
    });
  }

  return t('Address created successfully');
};

export const useAddressForm = ({ address, onSubmit }: { address: Address | undefined; onSubmit: () => void }) => {
  const { t } = useTranslation('phone', { keyPrefix: 'e911' });

  return useForm({
    fields: {
      name: { type: 'text', required: true, value: address?.name },
      street: {
        type: 'text',
        required: true,
        value: address?.street,
        validator: ({ value }: { value: string }) =>
          value.length > 60 ? t('Street address must be 60 characters or fewer.') : '',
      },
      streetSecondary: {
        type: 'text',
        value: address?.streetSecondary,
      },
      city: { type: 'text', required: true, value: address?.city },
      postalCode: {
        type: 'postalCode',
        required: true,
        value: address?.postalCode ?? PostalCodeLocale.US,
      },
      country: {
        type: 'dropdown',
        required: true,
        value: address?.country ?? CountryCodes.USA,
      },
      state: { type: 'dropdown', required: true, value: address?.state },
    },
    fieldStateReducer: (state, action) => {
      if (
        action.type === FormFieldActionTypes.Update &&
        action.payload.name === 'country' &&
        action.payload.value !== state[action.payload.name]
      ) {
        state[action.payload.name].value = action.payload.value;
        // If the country value is changed, clear the postal and state field values and
        // also change the postal locale to that of the country value (country code).
        return {
          ...state,
          postalCode: { ...state.postalCode, value: '', locale: action.payload.value },
          state: { ...state.state, value: '' },
        };
      }
      return state;
    },
    onSubmit,
  });
};

export const useCreateE911Address = ({
  tenantId,
  onSuccess,
}: {
  tenantId?: string;
  onSuccess: (address: CreatedE911Address, submittedAddress: Address) => void;
}) => {
  const [addressSaveError, setAddressSaveError] = useState<AddressSaveError>();

  const handleAddressSaveError = (error: unknown) => {
    setAddressSaveError((prev) => ({
      key: (prev?.key ?? 0) + 1,
      variant: isInvalidE911AddressError(error) ? 'invalid-address' : 'generic',
    }));
    logError('useCreateE911Address:createAddressMutation | Failed to create E911 address', {
      data: { tenantId, error },
    });
  };

  const clearAddressSaveError = () => {
    setAddressSaveError(undefined);
  };

  const { mutate: createAddress, isLoading: isCreatingAddress } = useMutation({
    mutationFn: (address: Address) => E911Api.create({ ...address, tenantId: tenantId ?? '' }),
    onSuccess: (createdAddress, submittedAddress) => onSuccess(createdAddress, submittedAddress),
    onError: handleAddressSaveError,
  });

  return { createAddress, isCreatingAddress, addressSaveError, handleAddressSaveError, clearAddressSaveError };
};
